import { useCallback, useEffect, useRef } from "react";
import { plan_product_pricings_prices_path } from "@/lib/routes";
import type { CellRef } from "./useSaveStatus";
import type { Group } from "../components/PricingTable";

export interface PriceCellUpdate {
  product_external_id: string;
  // A price level id, or "asp" for the ASP column.
  level: string;
  // null clears the cell.
  price: number | null;
}

interface Args {
  marketSlug: string;
  planSlug: string;
  channelSlug: string;
  currency: string;
  setLocalGroups: React.Dispatch<React.SetStateAction<Group[]>>;
  markSaved: (cells: CellRef[]) => void;
  markError: (cells: CellRef[]) => void;
}

const FLUSH_DELAY_MS = 400;

// Cell refs reuse the Bottom Up save-status shape; the level id (or "asp")
// rides in the periodDate slot.
function toCellRef(u: PriceCellUpdate): CellRef {
  return { productExternalId: u.product_external_id, periodDate: u.level };
}

// The pricing table's version of useCellSaver: optimistically applies price
// edits to local groups, queues them keyed per cell so a re-edit before flush
// keeps only the last value, and persists the queue in one batched PATCH
// after a short debounce.
export function usePricingCellSaver({
  marketSlug,
  planSlug,
  channelSlug,
  currency,
  setLocalGroups,
  markSaved,
  markError,
}: Args) {
  const queueRef = useRef<Map<string, PriceCellUpdate>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const updates = Array.from(queueRef.current.values());
    queueRef.current = new Map();
    if (updates.length === 0) return;

    const cells = updates.map(toCellRef);
    const csrfToken =
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
    fetch(plan_product_pricings_prices_path(marketSlug, planSlug, channelSlug), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ cells: updates }),
    })
      .then((res) => (res.ok ? markSaved(cells) : markError(cells)))
      .catch(() => markError(cells));
  }, [marketSlug, planSlug, channelSlug, markSaved, markError]);

  const saveCell = useCallback(
    (update: PriceCellUpdate) => {
      queueRef.current.set(`${update.product_external_id}|${update.level}`, update);

      const cell = update.price === null ? null : { price: update.price, currency };
      setLocalGroups((prev) =>
        prev.map((group) => {
          if (!group.rows.some((r) => r.product_external_id === update.product_external_id)) {
            return group;
          }
          return {
            ...group,
            rows: group.rows.map((row) => {
              if (row.product_external_id !== update.product_external_id) return row;
              if (update.level === "asp") return { ...row, asp: cell };
              return {
                ...row,
                prices_by_level_id: { ...row.prices_by_level_id, [update.level]: cell },
              };
            }),
          };
        }),
      );

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, FLUSH_DELAY_MS);
    },
    [currency, setLocalGroups, flush],
  );

  // Don't leave queued edits behind on unmount (channel navigation).
  useEffect(() => flush, [flush]);

  return { saveCell };
}
